import useClientStore from '@/store/useClientStore';
import { supabase } from '@/lib/supabase';
import { getVisitorId } from '@/lib/visitor-id';

/**
 * Registro de conversiones del sitio público en `public.site_events`
 * (insert con la anon key, fire-and-forget, igual que `page_visits`).
 *
 * Reglas:
 * - NUNCA bloquea la navegación: no se espera el insert (`void trackSiteEvent(...)`).
 * - NUNCA lanza: cualquier fallo se traga con console.error.
 * - Dedupe por sesión: una conversión por (tenant, tipo, página) por pestaña.
 *   3 clics seguidos en el mismo botón = 1 fila.
 * - Solo browser: en SSR/RSC no hace nada.
 */
export type SiteEventType = 'whatsapp_click' | 'call_click' | (string & {});

export interface TrackSiteEventOptions {
  vehicleId?: number | string | null;
  metadata?: Record<string, unknown>;
}

// Espejo del CHECK de la tabla: event_type ~ '^[a-z][a-z0-9_]{1,39}$'
const EVENT_TYPE_RE = /^[a-z][a-z0-9_]{1,39}$/;

/** `/vehicles/123` → 123. Cualquier otra ruta → null. */
export function vehicleIdFromPathname(pathname?: string | null): number | null {
  const p = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  const m = /^\/vehicles\/(\d+)(?:[/?#]|$)/.exec(p);
  return m ? Number(m[1]) : null;
}

/**
 * Clasifica un href/URL como conversión. Devuelve el event_type o null.
 * - WhatsApp: https://wa.me, https://api.whatsapp.com, whatsapp:
 * - Llamada:  tel:
 */
export function conversionTypeFromHref(href?: string | null): SiteEventType | null {
  const h = (href || '').trim().toLowerCase();
  if (!h) return null;
  if (
    h.startsWith('https://wa.me') ||
    h.startsWith('http://wa.me') ||
    h.startsWith('https://api.whatsapp.com') ||
    h.startsWith('https://web.whatsapp.com') ||
    h.startsWith('whatsapp:')
  ) {
    return 'whatsapp_click';
  }
  if (h.startsWith('tel:')) return 'call_click';
  return null;
}

function normalizeVehicleId(v: TrackSiteEventOptions['vehicleId']): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Claves con insert en vuelo (misma pestaña): segunda barrera del dedupe. */
const inFlight = new Set<string>();

export async function trackSiteEvent(
  eventType: SiteEventType,
  opts: TrackSiteEventOptions = {}
): Promise<void> {
  try {
    if (typeof window === 'undefined') return;
    if (!EVENT_TYPE_RE.test(eventType)) {
      console.error('trackSiteEvent: event_type inválido', eventType);
      return;
    }

    // zustand fuera de React: getState() en el hook-store
    const rawClientId = useClientStore.getState().client?.id;
    if (rawClientId === undefined || rawClientId === null || rawClientId === '') return;
    const clientIdNum = Number(rawClientId);
    const clientId = Number.isFinite(clientIdNum) ? clientIdNum : rawClientId;

    const pathname = window.location.pathname;
    // Rutas internas del sitio (preview del builder, iframe embebido): no son
    // conversiones del cliente final.
    if (pathname.startsWith('/builder') || pathname.startsWith('/embed')) return;
    const sessionKey = `site_event_${clientId}_${eventType}_${pathname}`;

    // Dedupe por sesión. El flag se marca ANTES del insert (y se limpia si
    // falla): con doble clic/doble tap las dos llamadas leían el flag vacío
    // durante la latencia del insert y se guardaban 2 filas.
    if (inFlight.has(sessionKey)) return;
    let dedupeAvailable = true;
    try {
      if (sessionStorage.getItem(sessionKey)) return;
      sessionStorage.setItem(sessionKey, 'true');
    } catch {
      dedupeAvailable = false;
    }
    inFlight.add(sessionKey);

    const vehicleId =
      normalizeVehicleId(opts.vehicleId) ?? vehicleIdFromPathname(pathname);

    const row = {
      client_id: clientId,
      visitor_id: getVisitorId() || null,
      event_type: eventType,
      pathname,
      vehicle_id: vehicleId,
      metadata: opts.metadata ?? null,
    };

    const { error } = await supabase.from('site_events').insert([row]);
    inFlight.delete(sessionKey);
    if (error) {
      console.error('Error tracking site event:', error);
      if (dedupeAvailable) {
        try {
          sessionStorage.removeItem(sessionKey);
        } catch {
          /* nada */
        }
      }
    }
  } catch (error) {
    console.error('Error tracking site event:', error);
  }
}

/**
 * Atajo para los sitios que abren WhatsApp/tel de forma programática
 * (window.open, location.href) en vez de un `<a href>`: clasifica el href y,
 * si es una conversión, la registra sin esperar. Devuelve el tipo o null.
 * NO usar en `<a href>`: esos ya los captura SiteEventTracker (doble conteo).
 */
export function trackConversionFromHref(
  href?: string | null,
  opts: TrackSiteEventOptions = {}
): SiteEventType | null {
  const type = conversionTypeFromHref(href);
  if (type) {
    void trackSiteEvent(type, {
      ...opts,
      metadata: { href: (href || '').trim(), source: 'programmatic', ...(opts.metadata ?? {}) },
    });
  }
  return type;
}
